import React from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import trimStart from 'lodash/trimStart';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import Icon from '../../../components/Icon';
import Modal from '../../../components/Modal';
import ClosableModal from '../../../components/Modal/ClosableModal';
import Button from '../../../components/Button';
import Loader from '../../../components/Loader';
import WYSIWYG from '../../../components/WYSIWYG';
import MediaCarousel from '../../../components/MediaCarousel';

import { HEM_IMAGE_INSTRUCTIONS_FEATURE } from '../../Authorization/constants';
import { withEnabledFeatures } from '../../../utils/features';
import * as actions from '../actions';
import EditVideo from './editVideos';
import messages from '../messages';
import DeletionModal from '../DeletionModal';
import ImageDropZoneWithCrop from '../ImageDropZoneWithCrop';

import './edit-modal-styles.css';

const parseTileIndex = (tileIndex) => (Number.isNaN(tileIndex) ? 0 : parseInt(tileIndex, 10));

export class EditPhotosAndVideosModalComponent extends React.Component {
  static propTypes = {
    onCloseModal: PropTypes.func.isRequired,
    openModal: PropTypes.bool.isRequired,
    media: PropTypes.array,
    photoUpload: PropTypes.func.isRequired,
    savePhotoCaption: PropTypes.func,
    addPhotoCroppedImageBlob: PropTypes.func,
    addMediaPhotoS3ScannedURI: PropTypes.func,
    setVideo: PropTypes.func,
    enabledFeatures: PropTypes.arrayOf(PropTypes.string),
  };

  initialState = {
    tileSelected: null,
    showAddTile: false,
    showImageUploadPrompt: false,
    showVideoSetPrompt: false,
    showCaptionContent: false,
    showCaptionContentEdit: false,
    showContinueEditingPhotos: false,
    showEditPhoto: false,
    showEditVideo: false,
    showDeletionPopup: false,
    corruptedUpload: false,
    croppedImageBlob: null, // has the cropped image url
    body: null,
    ImageState: null,
    uploading: false,
    captionNextButtonDisabled: false,
    nonPersistedPhotoCaption: '<p><br /></p>',
    allowTileChange: true,
  };

  state = this.initialState;

  goBackVideo = () => {
    this.setState({ showVideoSetPrompt: false, allowTileChange: true });
  }

  goBackCaption = () => {
    if (this.state.showCaptionContent) {
      this.setState({ showCaptionContent: false, showImageUploadPrompt: true });
    } else if (this.state.showCaptionContentEdit) {
      this.setState({ showCaptionContentEdit: false, showEditPhoto: true, allowTileChange: true });
    }
  }

  saveCaption = () => {
    const { nonPersistedPhotoCaption } = this.state;
    const photoIndex = parseTileIndex(get(this.state.tileSelected, 'id'), 10);
    this.props.savePhotoCaption({ caption: nonPersistedPhotoCaption, photoIndex });
  }

  saveCaptionAndClose = () => {
    this.saveCaption();
    this.showContinueEditingPhotos();
  }

  onChangePhotoCaption = (caption, isValid) => {
    if (!isValid) {
      this.setState({ captionNextButtonDisabled: true, nonPersistedPhotoCaption: caption });
    } else {
      this.setState({ captionNextButtonDisabled: false, nonPersistedPhotoCaption: caption });
    }
  }

  setTileSelected = (data) => {
    const caption = this.getCurrentPhotoCaption(get(data, 'id'));
    const mediaType = get(data, 'media_type');
    if (this.state.allowTileChange) {
      if (mediaType !== 'VIDEO_URL') {
        this.setState({
          tileSelected: data,
          showAddTile: !mediaType,
          showEditVideo: false,
          showVideoSetPrompt: false,
          showContinueEditingPhotos: false,
          showEditPhoto: !!mediaType,
          nonPersistedPhotoCaption: caption,
        });
      } else {
        this.setState({
          showAddTile: false,
          showEditVideo: true,
          tileSelected: data,
          showContinueEditingPhotos: false,
          showEditPhoto: false,
          nonPersistedPhotoCaption: caption,
        });
      }
    }
  }

  showAddTileContent = () => {
    this.setState({ showEditVideo: false, showVideoSetPrompt: false, showAddTile: true, showEditPhoto: false });
  }

  resetModalState = () => {
    this.setState(this.initialState);
  }

  showImageUploadPrompt = () => {
    this.setState({ showImageUploadPrompt: true, showAddTile: false, allowTileChange: false });
  }

  showCaptionContent = (ImageState, editMode) => {
    // set the flag to show the caption content, but also save the state of the ImageDropZoneWithCrop
    this.setState({
      showCaptionContent: !editMode,
      showCaptionContentEdit: !!editMode,
      showImageUploadPrompt: false,
      showEditPhoto: false,
      allowTileChange: false,
      ImageState,
    });
  }

  showContinueEditingPhotos = () => {
    this.setState({
      showEditPhoto: false,
      showContinueEditingPhotos: true,
      showCaptionContent: false,
      showImageUploadPrompt: false,
      showVideoSetPrompt: false,
      allowTileChange: true,
    });
  }

  savePhoto = async () => {
    const { ImageState } = this.state;

    this.saveCaption();
    const { croppedImageBlobDataStr } = ImageState;

    this.setState({ uploading: true });
    // run the mutation to scan the file for viruses and upload to s3
    try {
      const photoUploadResult = await this.props.photoUpload({
        variables: {
          input: {
            imgDataStr: croppedImageBlobDataStr,
          },
        },
      });
      // until this point we are sure that we need to save on state
      const targetIndex = parseTileIndex(get(this.state.tileSelected, 'id'), 10);

      this.props.addPhotoCroppedImageBlob(ImageState.croppedImageBlob, targetIndex);

      this.setState({ uploading: false });
      const imageDataResult =
        photoUploadResult.data.uploadPremiumHEMWebTourImage;

      if (imageDataResult.scanAndUploadSuccess) {
        this.props.addMediaPhotoS3ScannedURI(imageDataResult.imgPath, targetIndex);
        this.setState({ ImageState: null, tileSelected: null, centerImage: targetIndex });
      }
    } catch (err) {
      // assume a bad upload if the mutation failed
      this.setState({ uploading: false });

      return;
    }
    this.showContinueEditingPhotos();
  }

  saveVideo = (changes) => {
    const { tileSelected } = this.state;
    const targetIndex = parseTileIndex(get(tileSelected, 'id'), 10);
    this.props.setVideo(changes, targetIndex);
    this.setState({ tileSelected: null, centerImage: targetIndex });
    this.showContinueEditingPhotos();
  }

  showVideoSetPrompt = () => {
    this.setState({ showVideoSetPrompt: true, allowTileChange: false });
  }

  nevermindAction = () => {
    this.setState({ showImageUploadPrompt: false, showAddTile: true, allowTileChange: true });
  }

  editVideo = () => {
    this.showVideoSetPrompt();
  }

  showDeletionPopup = () => {
    this.setState({ showDeletionPopup: true });
  }

  closeDeletionModal = () => {
    this.setState({ showDeletionPopup: false });
  }

  closeModal = () => {
    const { tileSelected } = this.state;
    this.setState({ ...this.initialState, ...(tileSelected ? { tileSelected: tileSelected.id } : {}) });
    this.props.onCloseModal();
  }

  getCurrentPhotoCaption = (tileSelectedId) => {
    const { media } = this.props;
    const photoCaption = get(media, `[${tileSelectedId}].caption`, '');
    return photoCaption;
  }

  getBottomArea = () => {
    const {
      tileSelected,
      showAddTile,
      showImageUploadPrompt,
      showVideoSetPrompt,
      showCaptionContent,
      showCaptionContentEdit,
      showEditPhoto,
      showContinueEditingPhotos,
      ImageState,
      captionNextButtonDisabled,
      showEditVideo,
      nonPersistedPhotoCaption,
    } = this.state;
    const selectedCaption = get(tileSelected, 'caption');
    const selectedCaptionString = trimStart(selectedCaption) === '' ? '<p><br /></p>' : selectedCaption;
    const nonPersistedCaptionString = trimStart(nonPersistedPhotoCaption) === '' ? '<p><br /></p>' : nonPersistedPhotoCaption;
    const hasChanges = ImageState || (selectedCaptionString !== nonPersistedCaptionString);
    let modalBottomArea;

    if (showImageUploadPrompt && !isEmpty(tileSelected)) {
      modalBottomArea = (
        <ImageDropZoneWithCrop
          crop={{
            aspect: 8/5,
          }}
          nevermindAction={this.nevermindAction}
          nextButtonText={messages.next}
          nextButtonAction={this.showCaptionContent}
          savedState={ImageState}
          headerText={messages.cropPhotoInstructions1}
          instructionText={messages.cropPhotoInstructions2}
          allowedTypes={['image/png', 'image/jpeg', 'image/jpg']}
          uploaderInstructions1={messages.editPhotosAndVideosModalInstructions1}
          uploaderInstructions2={messages.editAboutUsPhotoModalInstructions2}
        />
      );
    } else if (showVideoSetPrompt) {
      modalBottomArea = (
        <EditVideo
          selectedIndex={parseInt(get(tileSelected, 'id'), 10)}
          initialValues={tileSelected}
          goBackVideo={this.goBackVideo}
          onNext={this.saveVideo}
        />
      );
    } else if (showEditPhoto) {
      modalBottomArea = (
        <React.Fragment>
          <Modal.Header styleName="modal-header-title">
            <FormattedMessage tagName="h3" {...messages.editPhotoHeader} />
          </Modal.Header>
          <Modal.Content styleName="photos-modal-content">
            <div styleName="edit-photo-video-options">
              <Button id="edit-photo-edit-button" onClick={() => this.showCaptionContent(null, true)}>
                <FormattedMessage {...messages.editCaptionButton} />
              </Button>
              <Button id="edit-photo-replace-button" onClick={this.showAddTileContent}>
                <FormattedMessage {...messages.replace} />
              </Button>
              <br />
              <div styleName="edit-photo-video-remove">
                <div styleName="edit-photo-video-remove-text">
                  <FormattedMessage {...messages.removeTile} />
                </div>
                <Button id="edit-photo-delete-button" negative compact onClick={this.showDeletionPopup}>
                  Delete
                </Button>
              </div>
            </div>
          </Modal.Content>
        </React.Fragment>
      );
    } else if (showContinueEditingPhotos) {
      modalBottomArea = (
        <React.Fragment>
          <Modal.Content>
            <div styleName="continue-editing-container">
              <div styleName="instructions continue-editing-element">
                <FormattedMessage tagName="h3" {...messages.continueAddingInstructions} />
              </div>
              <div styleName="instructions continue-editing-element">
                <FormattedMessage tagName="h3" {...messages.continueAddingOr} />
              </div>
              <div styleName="continue-editing-element">
                <Button
                  size="large"
                  id="preview-profile-button"
                  styleName="preview-profile-button"
                  onClick={this.closeModal}
                >
                  <FormattedMessage {...messages.previewAndContinueEditingButton} />
                </Button>
              </div>
            </div>
          </Modal.Content>
        </React.Fragment>
      );
    } else if (showCaptionContent || showCaptionContentEdit) {
      modalBottomArea = (
        <React.Fragment>
          <Modal.Header styleName="modal-header-title">
            <FormattedMessage {...messages.optionalCaption} />
          </Modal.Header>
          <Modal.Content styleName="photos-modal-content">
            <div styleName="photos-wysiwyg">
              <WYSIWYG
                value={nonPersistedPhotoCaption}
                limit={280}
                mode="normal"
                onChange={this.onChangePhotoCaption}
              />
            </div>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={this.goBackCaption}>
              <FormattedMessage {...messages.back} />
            </Button>
            {showCaptionContentEdit ? (
              <Button
                id="image-caption-save-button"
                primary
                disabled={!hasChanges || captionNextButtonDisabled}
                onClick={this.saveCaptionAndClose}
              >
                <FormattedMessage {...messages.save} />
              </Button>
            ) : (
              <Button
                id="image-caption-next-button"
                primary
                disabled={!hasChanges || captionNextButtonDisabled}
                onClick={this.savePhoto}
              >
                <FormattedMessage {...messages.next} />
              </Button>
            )}
          </Modal.Actions>
        </React.Fragment>
      );
    } else if (showAddTile) {
      modalBottomArea = (
        <Modal.Content styleName="photos-modal-content">
          <div styleName="photos-instructions">
            <span styleName="instructions"><FormattedMessage tagName="h3" {...messages.photosPrompt} /></span>
            <Button id="photos-modal-button" styleName="photos-modal-button" onClick={this.showImageUploadPrompt}>
              <FormattedMessage {...messages.photoButton} />
            </Button>
            <Button id="videos-modal-button" styleName="photos-modal-button" onClick={this.showVideoSetPrompt}>
              <FormattedMessage {...messages.videoButton} />
            </Button>
          </div>
        </Modal.Content>
      );
    } else if (showEditVideo) {
      modalBottomArea = (
        <React.Fragment>
          <Modal.Header styleName="modal-header-title">
            <FormattedMessage {...messages.editVideoHeader} />
          </Modal.Header>
          <Modal.Content styleName="photos-modal-content">
            <div styleName="edit-photo-video-options">
              <Button id="edit-video-edit-button" onClick={this.editVideo}>
                <FormattedMessage {...messages.editButton} />
              </Button>
              <Button id="edit-video-replace-button" onClick={this.showImageUploadPrompt}>
                <FormattedMessage {...messages.replaceWithPhoto} />
              </Button>
              <br />
              <div styleName="edit-photo-video-remove">
                <div styleName="edit-photo-video-remove-text">
                  <FormattedMessage {...messages.removeTile} />
                </div>
                <Button id="edit-video-delete-button" negative compact onClick={this.showDeletionPopup}>
                  Delete
                </Button>
              </div>
            </div>
          </Modal.Content>
        </React.Fragment>
      );
    } else {
      modalBottomArea = (
        <Modal.Content styleName="photos-modal-content">
          <div styleName="photos-instructions" data-cy="Premium-Hem-Photos-Modal-Content-Instructions">
            <span styleName="instructions">
              {this.props.enabledFeatures?.includes(HEM_IMAGE_INSTRUCTIONS_FEATURE) ? <FormattedMessage tagName="h3" {...messages.photosInstructionsV2} /> : <FormattedMessage tagName="h3" {...messages.photosInstructions} /> }
            </span>
          </div>
        </Modal.Content>
      );
    }
    return modalBottomArea;
  }

  render() {
    const { openModal, media } = this.props;
    const { tileSelected, ImageState, showDeletionPopup, centerImage = 0, uploading } = this.state;

    return (
      <ClosableModal
        open={openModal}
        onClose={this.closeModal}
      >
        { uploading ? (
          <div styleName="uploading-image-spinner">
            <Icon name="image outline" color="teal" size="massive" />
            <Loader active inline />
            <FormattedMessage {...messages.imageUploading} />
          </div>
        ): (
          <React.Fragment>
            <MediaCarousel
              isEditing
              tileSelected={tileSelected} // @TODO: make sure null checks from above apply
              setTileSelected={this.setTileSelected}
              resetModalState={this.resetModalState}
              fileDataStr={ImageState && ImageState.croppedImageBlobDataStr}
              media={media || []}
              centerImage={centerImage}
              enabledFeatures={this.props.enabledFeatures}
            />
            {this.getBottomArea()}
          </React.Fragment>
        )}
        {showDeletionPopup && (
          <DeletionModal
            closeModal={this.closeDeletionModal}
            resetModalState={this.resetModalState}
            openModal={showDeletionPopup}
            selectedId={parseTileIndex(get(tileSelected, 'id'), 10)}
          />
        )}
      </ClosableModal>
    );
  }
}

const mapStateToProps = (state) => ({
  media: get(state, 'nonPersistedCollegeProfile.media.media'),
  photoCaptions: get(state, 'nonPersistedCollegeProfile.media.photoCaptions'),
});

export default withEnabledFeatures(connect(mapStateToProps, { ...actions })(EditPhotosAndVideosModalComponent));
